import React, { useCallback } from 'react';

import { useDispatch } from 'react-redux';

import { getEpgNowNext } from 'config/api';

import { setNowNextByChannelId } from 'store/programs/actions';
import {
    NowNextResponse,
    RBChannel,
    RBParsedRail,
} from 'store/railsBuilder/reducer.types';

import { getCollectionData as requestCollectionData } from 'services/data-manager';
import { EventItemWithServiceInfo } from 'services/data-manager/types';

import NagraInstance from 'utils/axios/nagra/DefaultInstance';

import { CollectionItemsList } from 'screens/Collection/types';
import {
    OnSetStripeLoadingStatus,
    RailLoadingStatus,
} from 'screens/Modular/types';

type Params = {
    templateId: string | undefined;
    rail: RBParsedRail | undefined;
    channels: RBChannel[];
    isCollection: boolean;
    limit: number;
    setHasNext: React.Dispatch<React.SetStateAction<boolean>>;
    setData: React.Dispatch<React.SetStateAction<CollectionItemsList>>;
    setTotalItems: React.Dispatch<React.SetStateAction<number>>;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    onSetNextUpdateTime(timestamp: number): void;
    onSetStripeLoadingStatus?: OnSetStripeLoadingStatus;
};

function useLoadNowNext({
    templateId,
    rail,
    channels,
    isCollection,
    limit,
    onSetNextUpdateTime,
    setTotalItems,
    setPage,
    setHasNext,
    setData,
    onSetStripeLoadingStatus,
}: Params) {
    const dispatch = useDispatch();

    const onLoadNowNext = useCallback(
        async (silently: boolean) => {
            if (rail) {
                try {
                    if (!silently) {
                        onSetStripeLoadingStatus?.(
                            rail.id,
                            RailLoadingStatus.LOADING,
                        );
                    }

                    const channelsIds = channels
                        .slice(0, isCollection ? channels.length : limit)
                        .map(({ editorial: { id } }) => id);
                    const nowNextData =
                        await NagraInstance.get<NowNextResponse>(
                            getEpgNowNext({
                                channel: channelsIds.join(','),
                                next: true,
                            }),
                        );

                    const data = nowNextData.data.results.filter(
                        ({ programmes }) => programmes.length,
                    );

                    onSetNextUpdateTime(
                        Math.min(
                            ...data.map(
                                ({
                                    programmes: [
                                        {
                                            period: { end },
                                        },
                                    ],
                                }) => end * 1000,
                            ),
                        ),
                    );
                    setHasNext(channels.length > Number(rail.properties.limit));
                    setData(data);
                    setTotalItems(data.length);
                    setPage(1);

                    if (!data) {
                        onSetStripeLoadingStatus?.(
                            rail.id,
                            RailLoadingStatus.EMPTY,
                        );
                    } else {
                        onSetStripeLoadingStatus?.(
                            rail.id,
                            RailLoadingStatus.LOADED,
                        );
                        dispatch(setNowNextByChannelId(nowNextData.data));
                    }
                } catch (e) {
                    onSetStripeLoadingStatus?.(
                        rail.id,
                        RailLoadingStatus.FAILURE,
                    );
                    setData([]);
                    setTotalItems(0);
                }
            }
        },
        [
            rail,
            channels,
            isCollection,
            limit,
            onSetNextUpdateTime,
            setTotalItems,
            setPage,
            setHasNext,
            setData,
            onSetStripeLoadingStatus,
        ],
    );

    const onLoadKidsNowNext = useCallback(
        async (silently: boolean) => {
            if (rail && templateId) {
                try {
                    if (!silently) {
                        onSetStripeLoadingStatus?.(
                            rail.id,
                            RailLoadingStatus.LOADING,
                        );
                    }

                    const nowNextRail = await requestCollectionData({
                        templateId,
                        rail,
                        limit,
                        page: 0,
                    });
                    const kidsChannelsIds = nowNextRail.sections.reduce<
                        string[]
                    >((acc, section) => {
                        if (section) {
                            return [
                                ...acc,
                                ...section.contents
                                    .map(
                                        (
                                            item: EventItemWithServiceInfo | null,
                                        ) => item?.channel?.editorial?.id || '',
                                    )
                                    .filter(Boolean),
                            ];
                        }

                        return acc;
                    }, []);
                    const channelsIds = kidsChannelsIds.slice(
                        0,
                        isCollection ? kidsChannelsIds.length : limit,
                    );
                    const nowNextData =
                        await NagraInstance.get<NowNextResponse>(
                            getEpgNowNext({
                                channel: channelsIds.join(','),
                                next: true,
                            }),
                        );
                    const data = nowNextData.data.results.filter(
                        ({ programmes }) => programmes.length,
                    );

                    onSetNextUpdateTime(
                        Math.min(
                            ...data.map(
                                ({
                                    programmes: [
                                        {
                                            period: { end },
                                        },
                                    ],
                                }) => end * 1000,
                            ),
                        ),
                    );
                    setHasNext(
                        channelsIds.length > Number(rail.properties.limit),
                    );
                    setData(data);
                    setTotalItems(data.length);
                    setPage(1);

                    if (!data) {
                        onSetStripeLoadingStatus?.(
                            rail.id,
                            RailLoadingStatus.EMPTY,
                        );
                    } else {
                        onSetStripeLoadingStatus?.(
                            rail.id,
                            RailLoadingStatus.LOADED,
                        );
                        dispatch(setNowNextByChannelId(nowNextData.data));
                    }
                } catch (e) {
                    onSetStripeLoadingStatus?.(
                        rail.id,
                        RailLoadingStatus.FAILURE,
                    );
                    setData([]);
                    setTotalItems(0);
                    setPage(1);
                }
            }
        },
        [
            templateId,
            rail,
            isCollection,
            limit,
            onSetNextUpdateTime,
            setTotalItems,
            setPage,
            setHasNext,
            setData,
            onSetStripeLoadingStatus,
        ],
    );

    return { onLoadNowNext, onLoadKidsNowNext };
}

export default useLoadNowNext;
